'use client';

/**
 * TrialCtaSection — shared free-trial CTA block rendered below a plan grid.
 *
 * Extracted from ``PricingV2Section`` so the same rich trial call-to-action
 * can be re-used on both the Individuals tab (via PricingV2Section) and the
 * Lawyers tab (via ProfessionalPricingSection). The Firms tab is intentionally
 * excluded — no trial is offered for organizations.
 *
 * All copy is catalog-driven (``plan_translations`` + ``plan_features`` for
 * the ``trial`` plan tag): name, description, CTA, and feature bullets.
 * Marketing controls trial messaging via SQL UPDATE — no frontend deploy
 * needed. The section renders only when the trial plan exists in the catalog
 * with at least a name or description.
 *
 * Props:
 *   ``plans``       — the raw ServerPlan array (same source as the grid above;
 *                     the component picks out the ``individual`` trial row itself).
 *   ``locale``      — current locale for the 3-tier catalog fallback
 *                     (locale → en → undefined).
 *   ``onStartTrial``— CTA click handler (anonymous → signup modal,
 *                     authenticated → /chat).
 */

import { ArrowRight, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { ServerPlan } from '@/types/pricing';

import { PlanFeatureList } from './PlanFeatureList';

interface TrialCtaSectionProps {
  /** The full flat plan list — the same ``v2Plans`` / ``reduxPlans`` array the
   *  parent grid already holds. This component picks the trial row itself so
   *  the parent does not need to thread individual derived values. */
  plans: ServerPlan[];
  /** Current next-intl locale (e.g. ``'sk'`` / ``'en'``). */
  locale: string;
  /** Handler for the trial CTA button. The parent is responsible for routing
   *  (anon → signup modal, authenticated → /chat). */
  onStartTrial: () => void;
}

export function TrialCtaSection({
  plans,
  locale,
  onStartTrial,
}: TrialCtaSectionProps) {
  // Trial plan (price === 0) is filtered out of the paid grid above, but its
  // catalog data still drives this rich trial-CTA section below the grid.
  // Marketing controls all trial copy via plan_translations + plan_features
  // for the trial row — no frontend hardcoding.
  const trialServerPlan = plans.find(
    (p) => p.plan_type === 'individual' && p.price === 0
  );
  // 3-tier locale fallback (locale → en → undefined) mirrors the
  // catalogFeatures pattern — keeps the trial section visible in
  // dev/staging when marketing has only backfilled English copy.
  const trialCatalog =
    trialServerPlan?.translations?.[locale] ??
    trialServerPlan?.translations?.['en'];
  // All catalog feature rows for the trial plan — count is marketing's
  // decision (managed by adding/removing plan_features rows in the DB).
  const trialFeatures = (trialServerPlan?.catalogFeatures ?? [])
    .map((f) => f.translations[locale] ?? f.translations['en'] ?? '')
    .filter((text) => text.length > 0);
  // ``cta_label.default`` is the only variant used on this page.
  const trialCta = trialCatalog?.cta_label?.default;
  const trialName = trialCatalog?.display_name;
  const trialDescription = trialCatalog?.value_proposition;

  // Render guard: section only appears when the trial plan exists in the
  // catalog with at least a name or description.
  if (!trialServerPlan || (!trialName && !trialDescription)) {
    return null;
  }

  return (
    <div className='mt-10 max-w-3xl mx-auto rounded-2xl border border-line-default bg-gradient-to-br from-status-success/10 via-surface-card to-brand-tertiary/10 p-6'>
      <div className='flex flex-col items-center gap-4 text-center sm:flex-row sm:gap-6 sm:text-left'>
        <div className='flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-status-success/15 text-status-success'>
          <Sparkles className='h-6 w-6' aria-hidden='true' />
        </div>
        <div className='flex flex-1 flex-col gap-2'>
          {trialName && (
            <h3 className='text-base font-semibold text-content-primary'>
              {trialName}
            </h3>
          )}
          {trialDescription && (
            <p className='text-sm text-content-secondary'>{trialDescription}</p>
          )}
          {trialFeatures.length > 0 && (
            <PlanFeatureList
              features={trialFeatures}
              ariaLabel={`${trialName ?? 'Trial'} features`}
              checkVariant='success'
              className='mt-1'
            />
          )}
        </div>
        {trialCta && (
          <Button
            onClick={onStartTrial}
            variant='outline'
            className='whitespace-nowrap border-status-success/40 text-status-success-text hover:bg-status-success/10 hover:text-status-success-text min-h-[44px]'
          >
            {trialCta}
            <ArrowRight className='ml-1.5 h-4 w-4' aria-hidden='true' />
          </Button>
        )}
      </div>
    </div>
  );
}
